import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from './api.js';
import Chart from './Chart.jsx';
import {
  CATEGORIES, STAGE_LABEL, allMarkets, compact, countdown, meta, num, pct, pnlClass, price, signedUsd, usd, usdPerQuote,
} from './format.js';
import { Brand, Chevron, MarketIcon, Meter, Modal, SitePrefs, Stat, WalletChip, useIsMobile } from './ui.jsx';
import { t, tk, tErr, fmtDateTime, fmtTime } from './i18n.js';

const CAT_LABEL = { crypto: tk('Crypto'), metals: tk('Metals'), stocks: tk('Stocks'), indices: tk('Indices & ETFs'), commodities: tk('Commodities'), forex: tk('Forex') };
const catLabel = (c) => (CAT_LABEL[c.id] ? t(CAT_LABEL[c.id]) : c.label);
const STATUS_LABEL = {
  active: tk('active'), breached: tk('breached'), passed: tk('passed'), pending: tk('pending'), filled: tk('filled'), cancelled: tk('cancelled'),
  rejected: tk('rejected'), requested: tk('requested'), approved: tk('approved'), paid: tk('paid'), expired: tk('expired'),
};
const statusLabel = (x) => (STATUS_LABEL[x] ? t(STATUS_LABEL[x]) : x);

export default function Terminal(props) {
  const { account, prices, book, symbol, setSymbol, toast, favorites, toggleFavorite } = props;
  const mobile = useIsMobile();
  const [mtab, setMtab] = useState('chart');
  const [panelOpen, setPanelOpen] = useState(() => { try { return localStorage.getItem('marketsPanel') !== 'closed'; } catch { return true; } });
  useEffect(() => { try { localStorage.setItem('marketsPanel', panelOpen ? 'open' : 'closed'); } catch { /* ignore */ } }, [panelOpen]);
  const last = prices[symbol] && prices[symbol].price;
  const open = !prices[symbol] || prices[symbol].open !== false;
  const position = account.positions.find((p) => p.symbol === symbol) || null;

  // Price at which this position alone would trip the nearest loss rule.
  const breach = useMemo(() => {
    if (!position || position.markPrice == null) return null;
    const threshold = Math.max(account.dailyLoss.breachAt, account.drawdown.floor);
    const label = account.dailyLoss.breachAt >= account.drawdown.floor ? t('Daily loss limit') : t('Max drawdown limit');
    const k = usdPerQuote(symbol, prices) || 1;
    const dir = position.side === 'long' ? 1 : -1;
    const p = position.markPrice - (account.equity - threshold) / (position.qty * dir * k);
    return p > 0 ? { price: p, label } : null;
  }, [position, account, symbol, prices]);

  // Pending orders for the chart lines (refreshed every few seconds).
  const [pending, setPending] = useState([]);
  useEffect(() => {
    let alive = true;
    const load = () => api('GET', `/api/accounts/${account.id}/orders?status=pending`).then((o) => alive && setPending(o)).catch(() => {});
    load(); const t = setInterval(load, 3000);
    return () => { alive = false; clearInterval(t); };
  }, [account.id]);

  const [prefs, setPrefs] = usePrefs();
  useFillSounds(account, prefs.sound);
  const [help, setHelp] = useState(false);
  useShortcuts(prefs.keys, () => setHelp((h) => !h));

  const moveProtection = useCallback(async (kind, px) => {
    try {
      await api('PATCH', `/api/accounts/${account.id}/positions/${symbol}`, kind === 'sl' ? { slPrice: px } : { tpPrice: px });
      toast(kind === 'sl' ? t('Stop-loss moved to {price}', { price: price(symbol, px) }) : t('Take-profit moved to {price}', { price: price(symbol, px) }), 'success');
    } catch (e) { toast(tErr(e.message), 'error'); throw e; }
  }, [account.id, symbol, toast]);
  const quickOrder = useCallback(async (side, qty) => {
    const m = meta(symbol);
    try {
      const o = await api('POST', `/api/accounts/${account.id}/orders`, { symbol, side, type: 'market', qty, leverage: Math.min(prefs.qcLev || 5, account.maxLeverage, m.maxLeverage) });
      const vars = { qty: num(o.qty, m.qtyDp), base: m.base, price: price(symbol, o.fillPrice) };
      toast(side === 'buy' ? t('Bought {qty} {base} at {price}', vars) : t('Sold {qty} {base} at {price}', vars), 'success');
    } catch (e) { toast(tErr(e.message), 'error'); }
  }, [account.id, account.maxLeverage, symbol, toast, prefs.qcLev]);

  const chart = (
    <Chart symbol={symbol} lastPrice={last} position={position} breachPrice={breach && breach.price} breachLabel={breach && breach.label}
      orders={pending} onMoveProtection={position ? moveProtection : null} oneClick={prefs.oneClick && account.status === 'active' && open} onQuickOrder={quickOrder} />
  );
  const orderBook = <OrderBook book={book} symbol={symbol} last={last} stats={prices[symbol]} />;
  const orderPanel = <OrderPanel account={account} symbol={symbol} last={last} open={open} prices={prices} toast={toast} prefs={prefs} setPrefs={setPrefs} showHelp={() => setHelp(true)} />;
  const bottom = <BottomPanel account={account} toast={toast} setSymbol={(s) => { setSymbol(s); setMtab('chart'); }} prices={prices} />;
  const marketsPanel = (
    <MarketsPanel symbol={symbol} prices={prices} favorites={favorites} toggleFavorite={toggleFavorite}
      onPick={(s) => { setSymbol(s); setMtab('chart'); }} onClose={mobile ? null : () => setPanelOpen(false)} />
  );

  if (mobile) {
    return (
      <div className="app mobile">
        <TopBar {...props} last={last} mobile />
        <ChallengeStrip account={account} toast={toast} switcher={<AccountSwitcher {...props} />} />
        {account.status !== 'active' && <StatusBanner account={account} onNewChallenge={props.onNewChallenge} />}
        <div className="m-body">
          {mtab === 'markets' && marketsPanel}
          {mtab === 'chart' && <div className="m-chart">{chart}<QuickTrade onPick={() => setMtab('trade')} /></div>}
          {mtab === 'trade' && orderPanel}
          {mtab === 'book' && orderBook}
          {mtab === 'positions' && bottom}
        </div>
        <nav className="m-tabs" aria-label={t('Terminal sections')}>
          {[['markets', t('Markets')], ['chart', t('Chart')], ['trade', t('Trade')], ['book', t('Book')], ['positions', `${t('Positions')}${account.positions.length ? ` (${account.positions.length})` : ''}`]].map(([id, label]) => (
            <button key={id} className={'m-tab' + (mtab === id ? ' on' : '')} aria-current={mtab === id ? 'page' : undefined} onClick={() => setMtab(id)}>{label}</button>
          ))}
        </nav>
      </div>
    );
  }
  return (
    <div className="app">
      <TopBar {...props} last={last} />
      <ChallengeStrip account={account} toast={toast} />
      {account.status !== 'active' && <StatusBanner account={account} onNewChallenge={props.onNewChallenge} />}
      <div className={'workspace' + (panelOpen ? ' with-panel' : '')}>
        {panelOpen ? marketsPanel : <button className="panel-tab" onClick={() => setPanelOpen(true)} aria-label={t('Show markets list')}><span>{t('Markets')}</span></button>}
        {chart}
        {orderBook}
        {orderPanel}
      </div>
      {bottom}
      <LegalBar />
      {help && <ShortcutsModal onClose={() => setHelp(false)} />}
    </div>
  );
}

// ---------------------------------------------------------------- trader preferences, sounds, shortcuts
function usePrefs() {
  const [prefs, set] = useState(() => {
    try { return { oneClick: false, sound: true, keys: true, qcLev: 5, ...JSON.parse(localStorage.getItem('traderPrefs') || '{}') }; }
    catch { return { oneClick: false, sound: true, keys: true, qcLev: 5 }; }
  });
  const setPrefs = (patch) => set((p) => { const n = { ...p, ...patch }; try { localStorage.setItem('traderPrefs', JSON.stringify(n)); } catch { /* ignore */ } return n; });
  return [prefs, setPrefs];
}

let audioCtx = null;
export function beep(kind = 'fill') {
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    const tones = { fill: [880, 1320], close: [660, 440], alert: [440, 330, 440] }[kind] || [880];
    tones.forEach((f, i) => {
      const o = audioCtx.createOscillator(), g = audioCtx.createGain();
      o.type = 'sine'; o.frequency.value = f;
      const t = audioCtx.currentTime + i * 0.09;
      g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.12, t + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
      o.connect(g).connect(audioCtx.destination); o.start(t); o.stop(t + 0.13);
    });
  } catch { /* audio unavailable */ }
}

// Plays a short sound whenever a position opens, grows, shrinks or closes (market, limit, SL/TP fills).
function useFillSounds(account, on) {
  const prev = useRef(null);
  useEffect(() => {
    const sig = Object.fromEntries(account.positions.map((p) => [p.symbol, p.qty]));
    const before = prev.current;
    prev.current = { id: account.id, sig };
    if (!on || !before || before.id !== account.id) return;
    let grew = false, shrank = false;
    for (const [k, q] of Object.entries(sig)) { if (!(k in before.sig) || q > before.sig[k]) grew = true; else if (q < before.sig[k]) shrank = true; }
    for (const k of Object.keys(before.sig)) if (!(k in sig)) shrank = true;
    if (grew) beep('fill'); else if (shrank) beep('close');
  }, [account, on]);
}

const isTyping = (e) => {
  const t = e.target; const tag = t && t.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable);
};
// Keyboard shortcuts. They only switch controls and focus fields; they never place an order by themselves.
function useShortcuts(enabled, toggleHelp) {
  useEffect(() => {
    if (!enabled) return undefined;
    const f = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey || isTyping(e)) return;
      const k = e.key;
      const map = { b: 'buy', s: 'sell', m: 'market', l: 'limit', k: 'scaled', q: 'size' };
      if (k === '?') { e.preventDefault(); toggleHelp(); return; }
      if (map[k.toLowerCase()]) { e.preventDefault(); window.dispatchEvent(new CustomEvent('fx:shortcut', { detail: map[k.toLowerCase()] })); return; }
      if (/^[1-8]$/.test(k)) window.dispatchEvent(new CustomEvent('fx:timeframe', { detail: Number(k) - 1 }));
    };
    window.addEventListener('keydown', f);
    return () => window.removeEventListener('keydown', f);
  }, [enabled, toggleHelp]);
}

function ShortcutsModal({ onClose }) {
  const rows = [['B', t('Long (buy) side')], ['S', t('Short (sell) side')], ['M', t('Market order')], ['L', t('Limit order')], ['K', t('Scaled (ladder) order')],
    ['Q', t('Jump to the size field')], ['1 – 8', t('Timeframe: 1m, 5m, 15m, 1H, 4H, 1D, 1W, 1M')], ['?', t('Show or hide this list')], ['Enter', t('Submit the order form')]];
  return (
    <Modal title={t('Keyboard shortcuts')} onClose={onClose}>
      <div className="modal-body">
        <table className="keys"><tbody>{rows.map(([k, d]) => <tr key={k}><td><kbd>{k}</kbd></td><td>{d}</td></tr>)}</tbody></table>
        <p className="label">{t('Shortcuts never place an order on their own. You can turn them off in the order panel settings.')}</p>
      </div>
    </Modal>
  );
}

const LegalBar = () => (
  <div className="legal-bar">
    {t('Simulated trading with virtual funds. Evaluation fees are not deposits; virtual balances have no cash value. Copy-trading selection and rewards are discretionary.')}
    <a href="#/terms">{t('Terms & Risk Disclosure')}</a>
  </div>
);

export function Star({ on, onClick, label }) {
  return (
    <button type="button" className={'star' + (on ? ' on' : '')} aria-pressed={on} aria-label={label} title={on ? t('Remove from favorites') : t('Add to favorites')}
      onClick={(e) => { e.stopPropagation(); onClick(); }}>
      <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3.2l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17.2l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" /></svg>
    </button>
  );
}

// Always-visible markets list: category buttons, Favorites, search, stars.
function MarketsPanel({ symbol, prices, favorites, toggleFavorite, onPick, onClose }) {
  const [cat, setCat] = useState(() => { try { return localStorage.getItem('marketsCat') || 'all'; } catch { return 'all'; } });
  const [q, setQ] = useState('');
  const [sort, setSort] = useState(() => { try { return localStorage.getItem('marketsSort') || 'default'; } catch { return 'default'; } });
  useEffect(() => { try { localStorage.setItem('marketsCat', cat); } catch { /* ignore */ } }, [cat]);
  useEffect(() => { try { localStorage.setItem('marketsSort', sort); } catch { /* ignore */ } }, [sort]);
  const favSet = new Set(favorites);
  // Stable callbacks for the memoised rows (always call the latest handlers).
  const cb = useRef({}); cb.current = { onPick, toggleFavorite };
  const pick = useCallback((s) => cb.current.onPick(s), []);
  const togFav = useCallback((s) => cb.current.toggleFavorite(s), []);
  const needle = q.trim().toLowerCase().replace('/', '');
  const all = allMarkets();
  let rows = all.filter((x) => !needle || x.base.toLowerCase().includes(needle) || x.name.toLowerCase().includes(needle) || x.symbol.toLowerCase().includes(needle));
  if (!needle) rows = cat === 'fav' ? favorites.map((s) => all.find((x) => x.symbol === s)).filter(Boolean) : rows.filter((x) => cat === 'all' || x.category === cat);
  const val = (x, k) => { const p = prices[x.symbol]; return p ? Number(p[k]) || 0 : -Infinity; };
  if (sort === 'volume') rows = [...rows].sort((a, b) => val(b, 'quoteVolume') - val(a, 'quoteVolume'));
  else if (sort === 'gainers') rows = [...rows].sort((a, b) => val(b, 'changePct') - val(a, 'changePct'));
  else if (sort === 'losers') rows = [...rows].sort((a, b) => (val(a, 'changePct') === -Infinity ? Infinity : val(a, 'changePct')) - (val(b, 'changePct') === -Infinity ? Infinity : val(b, 'changePct')));
  else if (sort === 'az') rows = [...rows].sort((a, b) => (a.label || a.base).localeCompare(b.label || b.base));
  const cats = [{ id: 'fav', label: t('★ Favorites'), n: favorites.length }, { id: 'all', label: t('All'), n: all.length },
    ...CATEGORIES.map((c) => ({ ...c, label: catLabel(c), n: all.filter((x) => x.category === c.id).length })).filter((c) => c.n)];
  return (
    <section className="markets-panel" aria-label={t('Markets')}>
      <div className="mp-head">
        <span>{t('Markets')}</span>
        {onClose && <button className="icon-btn sm" onClick={onClose} aria-label={t('Hide markets list')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M15 6l-6 6 6 6" /></svg>
        </button>}
      </div>
      <div className="mp-cats" role="tablist" aria-label={t('Market categories')}>
        {cats.map((c) => (
          <button key={c.id} role="tab" aria-selected={!needle && cat === c.id} className={'mp-cat' + (!needle && cat === c.id ? ' on' : '') + (c.id === 'fav' ? ' fav' : '')} onClick={() => { setCat(c.id); setQ(''); }}>
            {c.label}<span className="count">{c.n}</span>
          </button>
        ))}
      </div>
      <div className="mp-search">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
        <input placeholder={t('Search')} value={q} onChange={(e) => setQ(e.target.value)} aria-label={t('Search markets')} />
      </div>
      <div className="mp-sort" role="group" aria-label={t('Sort markets')}>
        {[['default', t('Default')], ['volume', t('Volume')], ['gainers', t('Gainers')], ['losers', t('Losers')], ['az', t('A–Z')]].map(([id, label]) => (
          <button key={id} className={'mp-sortbtn' + (sort === id ? ' on' : '')} aria-pressed={sort === id} onClick={() => setSort(id)}>{label}</button>
        ))}
      </div>
      <div className="mp-list">
        {rows.length === 0 && (
          <div className="empty">{cat === 'fav' && !needle ? t('Tap the ☆ next to any market to add it to your favorites.') : t('No markets match.')}</div>
        )}
        {rows.map((x) => (
          <MarketRow key={x.symbol} x={x} p={prices[x.symbol]} on={x.symbol === symbol} fav={favSet.has(x.symbol)} vol={sort === 'volume'}
            onPick={pick} toggleFavorite={togFav} />
        ))}
      </div>
    </section>
  );
}

// One row of the markets list. Memoised: it only re-renders when its own price changes.
const MarketRow = React.memo(function MarketRow({ x, p, on, fav, vol, onPick, toggleFavorite }) {
  const closed = p && p.open === false;
  return (
    <div className={'mp-row' + (on ? ' on' : '')} role="button" tabIndex={0}
      onClick={() => onPick(x.symbol)} onKeyDown={(e) => { if (e.key === 'Enter') onPick(x.symbol); }}>
      <Star on={fav} onClick={() => toggleFavorite(x.symbol)} label={t('Favorite {market}', { market: x.label || x.base })} />
      <MarketIcon symbol={x.symbol} size={20} />
      <span className="mp-name"><b>{x.label || x.base}</b><span className="muted">{x.name}</span></span>
      <span className="mp-px">
        <span className="mono">{price(x.symbol, p && p.price)}</span>
        <span className={'mono small ' + (closed ? 'muted' : pnlClass(p && p.changePct))}>{closed ? t('Closed') : vol ? <span className="muted">{t('Vol {volume}', { volume: compact(p && p.quoteVolume) })}</span> : pct(p && p.changePct)}</span>
      </span>
    </div>
  );
}, (a, b) => a.x === b.x && a.p === b.p && a.on === b.on && a.fav === b.fav && a.vol === b.vol);

const QuickTrade = ({ onPick }) => (
  <div className="quick-trade">
    <button className="btn submit long" onClick={onPick}>{t('Long')}</button>
    <button className="btn submit short" onClick={onPick}>{t('Short')}</button>
  </div>
);

// ---------------------------------------------------------------- top bar
function TopBar({ user, accounts, account, setAccountId, prices, symbol, setSymbol, live, feed, signOut, onNewChallenge, go, last, mobile, favorites, toggleFavorite }) {
  const [menu, setMenu] = useState(null);
  const st = prices[symbol] || {};
  const m = meta(symbol);
  const prevRef = useRef(last);
  const [flash, setFlash] = useState('');
  useEffect(() => {
    if (prevRef.current != null && last != null && last !== prevRef.current) {
      setFlash(last > prevRef.current ? 'up' : 'down');
      const t = setTimeout(() => setFlash(''), 700);
      prevRef.current = last;
      return () => clearTimeout(t);
    }
    prevRef.current = last;
    return undefined;
  }, [last]);
  useEffect(() => {
    if (!menu || menu === 'mkt') return undefined;
    const close = () => setMenu(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [menu]);
  const feedOk = live && feed.connected;
  const isOpen = st.open !== false;

  const accountMenu = (
    <div className="menu-wrap" onClick={(e) => e.stopPropagation()}>
      <button className="chip" onClick={() => setMenu(menu === 'acct' ? null : 'acct')} aria-expanded={menu === 'acct'} aria-label={t('Switch account')}>
        <span className="mono">#{account.id.slice(0, 6).toUpperCase()}</span>
        {!mobile && <span className="muted">{STAGE_LABEL[account.stage]}</span>}
        <Chevron />
      </button>
      {menu === 'acct' && (
        <div className="menu right">
          {accounts.map((a) => (
            <button key={a.id} className={'menu-item acct-row' + (a.id === account.id ? ' on' : '')} onClick={() => { setAccountId(a.id); setMenu(null); }}>
              <span className="mono">#{a.id.slice(0, 6).toUpperCase()}</span>
              <span className="muted">{a.templateName}</span>
              <span className={'badge ' + a.status}>{a.stage === 'funded' && a.status === 'active' ? t('stage 3') : statusLabel(a.status)}</span>
              <span className="mono">{usd(a.equity)}</span>
            </button>
          ))}
          <button className="menu-item accent" onClick={() => { setMenu(null); onNewChallenge(); }}>{t('+ New challenge')}</button>
        </div>
      )}
    </div>
  );

  if (mobile) {
    return (
      <header className="topbar m-top">
        <a className="brand compact" href="#/" aria-label={t('Home')}><svg width="22" height="22" viewBox="0 0 26 26" aria-hidden="true"><rect x="2" y="14" width="6" height="10" rx="1.5" fill="var(--accent)" /><rect x="10" y="8" width="6" height="16" rx="1.5" fill="var(--accent)" opacity="0.7" /><rect x="18" y="2" width="6" height="22" rx="1.5" fill="var(--accent)" opacity="0.45" /></svg></a>
        <MarketPicker symbol={symbol} setSymbol={setSymbol} prices={prices} open={menu === 'mkt'} setOpen={(o) => setMenu(o ? 'mkt' : null)} favorites={favorites} toggleFavorite={toggleFavorite} />
        <div className="last">
          <span className={'mono big ' + (flash || pnlClass(st.changePct))}>{price(symbol, last)}</span>
          <span className={'label ' + pnlClass(st.changePct)}>{isOpen ? pct(st.changePct) : t('Closed')}</span>
        </div>
        <div className="grow" />
        <WalletChip user={user} signOut={signOut} go={go} />
      </header>
    );
  }
  return (
    <header className="topbar">
      <Brand onClick={(e) => e.preventDefault()} />
      <div className="vsep" />
      <MarketPicker symbol={symbol} setSymbol={setSymbol} prices={prices} open={menu === 'mkt'} setOpen={(o) => setMenu(o ? 'mkt' : null)} favorites={favorites} toggleFavorite={toggleFavorite} />
      <div className="last">
        <span className={'mono big ' + (flash || pnlClass(st.changePct))}>{price(symbol, last)}</span>
        <span className="label">{isOpen ? t('Last price') : t('Market closed')}</span>
      </div>
      <div className="stats">
        <Stat label={t('24h change')} value={<span className={pnlClass(st.change)}>{st.change == null ? '—' : `${st.change > 0 ? '+' : ''}${num(st.change, m.priceDp)} (${pct(st.changePct)})`}</span>} />
        <Stat label={t('24h high')} value={price(symbol, st.high)} />
        <Stat label={t('24h low')} value={price(symbol, st.low)} />
        {m.category === 'forex' ? <Stat label={t('Source')} value="Pyth" /> : <Stat label={t('24h volume')} value={compact(st.quoteVolume)} />}
        {m.category !== 'forex' && <Stat label={t('Funding / 8h')} value={st.funding == null ? '—' : `${num(st.funding * 100, 4)}%`} />}
      </div>
      <div className="grow" />
      <nav className="nav" aria-label={t('Main')}>
        <button className="nav-item on" aria-current="page">{t('Trade')}</button>
        <button className="nav-item" onClick={() => go('leaderboard')}>{t('Leaderboard')}</button>
        <button className="nav-item" onClick={() => go('affiliates')}>{t('Affiliates')}</button>
        {user.isAdmin && <button className="nav-item" onClick={() => go('admin')}>{t('Admin')}</button>}
      </nav>
      <span className={'feed ' + (feedOk ? 'ok' : 'bad')} title={feedOk ? t('Live prices connected') : t('Reconnecting to live prices')}>
        <span className={'dot ' + (feedOk ? 'ok' : 'bad')} /><span className="feed-text">{feedOk ? (feed.source === 'sim' ? t('Simulated') : t('Live')) : t('Reconnecting')}</span>
      </span>
      <SitePrefs />
      {accountMenu}
      <WalletChip user={user} signOut={signOut} go={go} />
    </header>
  );
}

// ---------------------------------------------------------------- market picker
function MarketPicker({ symbol, setSymbol, prices, open, setOpen, favorites = [], toggleFavorite = () => {} }) {
  const m = meta(symbol);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('all');
  const inputRef = useRef(null);
  useEffect(() => { if (open) { setQ(''); setTimeout(() => inputRef.current && inputRef.current.focus(), 0); } }, [open]);
  const needle = q.trim().toLowerCase().replace('/', '');
  // Typing searches every category; tabs only filter when the search box is empty.
  const favSet = new Set(favorites);
  const rows = allMarkets().filter((x) => (needle || cat === 'all' || (cat === 'fav' ? favSet.has(x.symbol) : x.category === cat)) &&
    (!needle || x.base.toLowerCase().includes(needle) || x.name.toLowerCase().includes(needle) || x.symbol.toLowerCase().includes(needle)));
  const pick = (s) => { setSymbol(s); setOpen(false); };
  return (
    <div className="menu-wrap">
      <button className="market-btn" onClick={() => setOpen(!open)} aria-expanded={open}>
        <MarketIcon symbol={symbol} size={20} />{m.name}
        <Chevron />
      </button>
      {open && (
        <>
          <div className="scrim" onClick={() => setOpen(false)} />
          <div className="picker" role="dialog" aria-label={t('Choose market')}>
            <div className="picker-search">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
              <input ref={inputRef} placeholder={t('Search: BTC, Gold, Tesla, EUR/USD…')} value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && rows[0]) pick(rows[0].symbol); if (e.key === 'Escape') setOpen(false); }} aria-label={t('Search markets')} />
              <button className="icon-btn picker-close" onClick={() => setOpen(false)} aria-label={t('Close')}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
              </button>
            </div>
            <div className="picker-cats" role="tablist">
              {[{ id: 'fav', label: t('★ Favorites') }, { id: 'all', label: t('All') }, ...CATEGORIES].map((c) => {
                const n = c.id === 'all' ? allMarkets().length : c.id === 'fav' ? favorites.length : allMarkets().filter((x) => x.category === c.id).length;
                if (!n && c.id !== 'fav') return null;
                return (
                  <button key={c.id} role="tab" aria-selected={!needle && cat === c.id} className={'pcat' + (!needle && cat === c.id ? ' on' : '')} onClick={() => { setCat(c.id); setQ(''); }}>
                    {catLabel(c)} <span className="count">{n}</span>
                  </button>
                );
              })}
            </div>
            <div className="picker-head"><span>{t('Market')}</span><span>{t('Last price')}</span><span>{t('24h change')}</span><span>{t('Max lev.')}</span></div>
            <div className="picker-list">
              {rows.length === 0 && <div className="empty">{cat === 'fav' && !needle ? t('Tap ☆ next to a market to add it to your favorites.') : t('No markets match “{query}”', { query: q })}</div>}
              {rows.map((x) => {
                const p = prices[x.symbol];
                const closed = p && p.open === false;
                return (
                  <button key={x.symbol} className={'picker-row' + (x.symbol === symbol ? ' on' : '')} onClick={() => pick(x.symbol)}>
                    <span className="row gap10"><Star on={favSet.has(x.symbol)} onClick={() => toggleFavorite(x.symbol)} label={t('Favorite {market}', { market: x.label || x.base })} /><MarketIcon symbol={x.symbol} size={24} /><span className="pname"><b>{x.label || x.base}</b><span className="muted">{x.name}</span></span></span>
                    <span className="mono">{price(x.symbol, p && p.price)}</span>
                    <span className={'mono ' + (closed ? 'muted' : pnlClass(p && p.changePct))}>{closed ? t('Closed') : pct(p && p.changePct)}</span>
                    <span className="mono muted">{x.maxLeverage}×</span>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- challenge strip
// Compact account switcher used on phones (inside the account card).
function AccountSwitcher({ accounts, account, setAccountId, onNewChallenge }) {
  return (
    <select className="acct-select" value={account.id} aria-label={t('Switch account')}
      onChange={(e) => (e.target.value === '__new' ? onNewChallenge() : setAccountId(e.target.value))}>
      {accounts.map((a) => <option key={a.id} value={a.id}>#{a.id.slice(0, 6).toUpperCase()} · {a.templateName.replace(' Challenge', '')}{a.status !== 'active' ? ` · ${statusLabel(a.status)}` : a.stage === 'funded' ? ` · ${t('Stage 3')}` : ''}</option>)}
      <option value="__new">{t('+ New challenge')}</option>
    </select>
  );
}

function ChallengeStrip({ account: a, toast, switcher }) {
  const [, tick] = useState(0);
  const [payout, setPayout] = useState(false);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 30000); return () => clearInterval(t); }, []);
  const dailyTone = a.dailyLoss.used / a.dailyLoss.limit > 0.7 ? 'danger' : 'warn';
  const ddTone = a.drawdown.used / a.drawdown.limit > 0.7 ? 'danger' : 'warn';
  const days = a.tradingDays;
  const funded = a.stage === 'funded';
  return (
    <section className="strip" aria-label={t('Challenge status')}>
      <div className="cell">
        <div className="row gap8">
          <span className={'stage ' + a.stage}>{STAGE_LABEL[a.stage]}</span>
          {switcher || <span className="label">{a.templateName} · #{a.id.slice(0, 6).toUpperCase()}</span>}
        </div>
        <div className="row gap10 baseline">
          <span className="mono xl" title={t('Virtual equity (simulated)')}>{usd(a.equity)}</span>
          <span className={'mono ' + pnlClass(a.profitPct)}>{pct(a.profitPct)}</span>
        </div>
        {a.netPnl != null && (
          <span className="label net-line" title={t('Your result if you closed everything now, after all trading fees (paid and to pay)')}>
            {t('Net PnL after fees')} <b className={'mono ' + pnlClass(a.netPnl)}>{signedUsd(a.netPnl)}</b>
          </span>
        )}
      </div>
      <div className="cell">
        {a.target ? (
          <>
            <div className="cell-head"><span className="label">{t('Profit target')}</span><span className="mono">{usd(a.target.closedProfit)} / {usd(a.target.amount, 0)}</span></div>
            <Meter value={Math.max(0, a.target.closedProfit)} max={a.target.amount} tone="accent" />
            <span className="label">{a.target.remaining > 0 ? t('{amount} closed profit to go', { amount: usd(a.target.remaining) }) : t('Target reached: close positions to pass')}</span>
          </>
        ) : (
          <>
            <div className="cell-head"><span className="label">{t('Stage 3 virtual profit')}</span><span className={'mono ' + pnlClass(a.profit)}>{signedUsd(a.profit)}</span></div>
            <div className="row gap8">
              <span className="label">{t('Eligible for reward review')}</span>
              {funded && a.status === 'active' && <button className="btn xs primary" onClick={() => setPayout(true)} disabled={a.profit <= 0}>{t('Request reward')}</button>}
            </div>
          </>
        )}
      </div>
      <div className="cell">
        <div className="cell-head"><span className="label">{t('Daily loss limit')}</span><span className="mono">{usd(a.dailyLoss.used, 0)} / {usd(a.dailyLoss.limit, 0)}</span></div>
        <Meter value={a.dailyLoss.used} max={a.dailyLoss.limit} tone={dailyTone} />
        <span className="label">{t('Resets 00:00 UTC · in {time}', { time: countdown(a.dailyLoss.resetsAt) })}</span>
      </div>
      <div className="cell">
        <div className="cell-head"><span className="label">{t('Max drawdown')}</span><span className="mono">{usd(a.drawdown.used, 0)} / {usd(a.drawdown.limit, 0)}</span></div>
        <Meter value={a.drawdown.used} max={a.drawdown.limit} tone={ddTone} />
        <span className="label">{t('Floor {floor} · {room} of room', { floor: usd(a.drawdown.floor, 0), room: usd(Math.max(0, a.equity - a.drawdown.floor), 0) })}</span>
      </div>
      <div className="cell">
        <div className="cell-head"><span className="label">{t('Trading days')}</span><span className="mono">{days.done} / {days.required}</span></div>
        <div className="days">
          {Array.from({ length: Math.min(days.required, 20) }, (_, i) => <div key={i} className={'day' + (i < days.done ? ' on' : '')} />)}
        </div>
        <span className="label">{funded ? t('Stage 3: performance program') : days.done >= days.required ? t('Minimum met') : days.required - days.done === 1 ? t('{n} more day to qualify', { n: days.required - days.done }) : t('{n} more days to qualify', { n: days.required - days.done })}</span>
      </div>
      {payout && <PayoutModal account={a} toast={toast} onClose={() => setPayout(false)} />}
    </section>
  );
}

function PayoutModal({ account: a, toast, onClose }) {
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const split = a.profitSplit || 0.8;
  const profit = Math.max(0, a.profit);
  const submit = async () => {
    setBusy(true);
    try {
      await api('POST', `/api/accounts/${a.id}/payouts`);
      toast(t('Reward request sent for review. You can follow it in the Rewards tab.'), 'success');
      onClose();
    } catch (e) { toast(tErr(e.message), 'error'); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={t('Request a reward review')} onClose={onClose}>
      <div className="modal-body">
        <div className="summary big">
          <div><span className="label">{t('Stage 3 virtual profit')}</span><span className="mono">{usd(profit)}</span></div>
          <div><span className="label">{t('Reward rate (up to)')}</span><span className="mono">{Math.round(split * 100)}%</span></div>
          <div className="total"><span>{t('Possible reward')}</span><span className="mono up">{t('up to {amount}', { amount: usd(profit * split) })}</span></div>
        </div>
        <ul className="fine">
          <li>{t('Your account is simulated. Virtual profit is a performance score, not money you own.')}</li>
          <li>{t('Rewards are discretionary. We review your trading, may ask you to verify your identity, and may approve a lower amount or decline without giving reasons.')}</li>
          <li>{t('When you submit, the virtual profit is set aside and your balance returns to {balance}. If the request is declined it is returned.', { balance: usd(a.initialBalance, 0) })}</li>
        </ul>
        <label className="check"><input type="checkbox" checked={ok} onChange={(e) => setOk(e.target.checked)} /><span>{(() => { const [pre, post = ''] = t('I understand and agree to the {terms}.').split('{terms}'); return <>{pre}<a href="#/terms" target="_blank" rel="noreferrer">{t('Terms')}</a>{post}</>; })()}</span></label>
        <button className="btn primary lg block" onClick={submit} disabled={busy || !ok || a.positions.length > 0}>
          {a.positions.length > 0 ? t('Close your positions first') : busy ? t('Sending…') : t('Send for review')}
        </button>
      </div>
    </Modal>
  );
}

function StatusBanner({ account, onNewChallenge }) {
  if (account.status === 'breached') {
    return (
      <div className="banner danger" role="alert">
        <span><b>{t('Challenge failed.')}</b> {account.breachReason ? tErr(account.breachReason) : ''} {t('All positions were closed.')}</span>
        <button className="btn sm" onClick={onNewChallenge}>{t('Start a new challenge')}</button>
      </div>
    );
  }
  return <div className="banner">{t('Account status: {status}', { status: statusLabel(account.status) })}</div>;
}

// ---------------------------------------------------------------- order book
function OrderBook({ book, symbol, last, stats }) {
  const m = meta(symbol);
  if (m.category === 'forex') {
    return (
      <section className="book" aria-label={t('Price')}>
        <div className="panel-head"><span>{t('Price')}</span></div>
        <div className="fx-quote">
          <MarketIcon symbol={symbol} size={34} />
          <div className="mono fx-px">{price(symbol, last)}</div>
          <div className="label">{m.fullName}</div>
          <div className="fx-spread">
            <div><span className="label">{t('Bid')}</span><span className="mono down">{price(symbol, last && stats && stats.conf ? last - stats.conf : last)}</span></div>
            <div><span className="label">{t('Ask')}</span><span className="mono up">{price(symbol, last && stats && stats.conf ? last + stats.conf : last)}</span></div>
          </div>
          <p className="muted small-p">{t('Forex is priced from the interbank feed (Pyth). There is no order book; orders fill at the live mid price. Forex closes from Friday 17:00 to Sunday 17:00 New York time.')}</p>
        </div>
      </section>
    );
  }
  const rows = 12;
  const asks = book && book.symbol === symbol ? book.asks.slice(0, rows) : [];
  const bids = book && book.symbol === symbol ? book.bids.slice(0, rows) : [];
  const cum = (list) => { let c = 0; return list.map(([p, q]) => { c += q; return [p, q, c]; }); };
  const a = cum(asks), b = cum(bids);
  const maxCum = Math.max(a.length ? a[a.length - 1][2] : 0, b.length ? b[b.length - 1][2] : 0) || 1;
  const spread = asks.length && bids.length ? asks[0][0] - bids[0][0] : null;
  const qdp = m.qtyDp;
  return (
    <section className="book" aria-label={t('Order book')}>
      <div className="panel-head"><span>{t('Order book')}</span></div>
      <div className="book-cols"><span>{t('Price (USD)')}</span><span>{t('Size ({base})', { base: m.base })}</span><span>{t('Total')}</span></div>
      <div className="book-side asks">
        {a.slice().reverse().map(([p, q, c]) => (
          <div key={'a' + p} className="book-row">
            <div className="depth ask" style={{ width: (c / maxCum) * 100 + '%' }} />
            <span className="down">{price(symbol, p)}</span><span>{num(q, qdp)}</span><span className="muted">{num(c, qdp)}</span>
          </div>
        ))}
      </div>
      <div className="book-mid">
        <span className={'mono mid ' + pnlClass(stats && stats.changePct)}>{price(symbol, last)}</span>
        <span className="label">{spread == null ? '' : t('Spread {value}', { value: num(spread, m.priceDp) })}</span>
      </div>
      <div className="book-side">
        {b.map(([p, q, c]) => (
          <div key={'b' + p} className="book-row">
            <div className="depth bid" style={{ width: (c / maxCum) * 100 + '%' }} />
            <span className="up">{price(symbol, p)}</span><span>{num(q, qdp)}</span><span className="muted">{num(c, qdp)}</span>
          </div>
        ))}
      </div>
      {!a.length && <div className="empty">{t('Waiting for order book…')}</div>}
    </section>
  );
}

// ---------------------------------------------------------------- order entry
function OrderPanel({ account, symbol, last, open, prices, toast, prefs, setPrefs, showHelp }) {
  const m = meta(symbol);
  const [side, setSide] = useState('buy');
  const [type, setType] = useState('market');
  const [qty, setQty] = useState('');
  const [limit, setLimit] = useState('');
  const [protect, setProtect] = useState(false);
  const [sl, setSl] = useState('');
  const [tp, setTp] = useState('');
  const maxLev = Math.min(account.maxLeverage, m.maxLeverage);
  const [lev, setLev] = useState(Math.min(5, maxLev));
  const [busy, setBusy] = useState(false);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [count, setCount] = useState('10');
  const [settings, setSettings] = useState(false);
  const sizeRef = useRef(null);
  useEffect(() => { setQty(''); setLimit(''); setSl(''); setTp(''); setFrom(''); setTo(''); setLev((l) => Math.min(l, maxLev)); }, [symbol, maxLev]);
  useEffect(() => {
    const f = (e) => {
      const d = e.detail;
      if (d === 'buy' || d === 'sell') { setSide(d); setTimeout(() => sizeRef.current && sizeRef.current.focus(), 0); }
      else if (d === 'market' || d === 'limit' || d === 'scaled') setType(d);
      else if (d === 'size') sizeRef.current && sizeRef.current.focus();
    };
    window.addEventListener('fx:shortcut', f);
    return () => window.removeEventListener('fx:shortcut', f);
  }, []);
  useEffect(() => { if (type === 'limit' && !limit && last) setLimit(String(last)); }, [type, limit, last]);

  const conv = usdPerQuote(symbol, prices) || 1;
  const q = Number(qty) || 0;
  const n = Math.floor(Number(count)) || 0;
  const f0 = Number(from) || 0, f1 = Number(to) || 0;
  const ladder = type === 'scaled' && n >= 2 && f0 > 0 && f1 > 0 && f0 !== f1
    ? Array.from({ length: n }, (_, i) => Number((f0 + (f1 - f0) * i / (n - 1)).toFixed(m.priceDp))) : [];
  const ladderAvg = ladder.length ? ladder.reduce((a, b) => a + b, 0) / ladder.length : 0;
  const eachQty = n ? Math.floor((q / n) / m.qtyStep + 1e-9) * m.qtyStep : 0;
  const px = type === 'limit' ? Number(limit) || 0 : type === 'scaled' ? ladderAvg : last || 0;
  const notional = q * px * conv;
  const margin = notional / lev;
  const feeRate = m.feeRate != null ? m.feeRate : account.feeRate;
  const fee = notional * feeRate;
  const feeRound = fee * 2; // opening + closing at the same price
  const maxQty = px > 0 ? Math.max(0, (account.freeMargin * lev) / (px * conv)) : 0;
  const lossRoom = Math.max(0, Math.min(account.dailyLoss.limit - account.dailyLoss.used, account.equity - account.drawdown.floor));
  const onePct = notional * 0.01;
  const tradable = account.status === 'active';
  const isLong = side === 'buy';
  const opp = account.positions.find((p) => p.symbol === symbol && (p.side === 'long') !== isLong);
  const opening = Math.max(0, q - (opp ? opp.qty : 0));
  const tooBig = opening > 0 && (opening * px * conv) / lev + fee > account.freeMargin + (opp ? opp.margin : 0) + 0.005;
  const slN = Number(sl) || null, tpN = Number(tp) || null;
  const atLevel = (lvl) => (lvl && px && q ? (isLong ? lvl - px : px - lvl) * q * conv : null);

  const setFrac = (f) => {
    const step = m.qtyStep;
    const v = Math.floor((maxQty * f) / step) * step;
    setQty(v > 0 ? v.toFixed(m.qtyDp) : '');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!q) return toast(t('Enter a size first.'), 'error');
    setBusy(true);
    if (type === 'scaled') {
      try {
        const r = await api('POST', `/api/accounts/${account.id}/orders/scaled`, { symbol, side, fromPrice: f0, toPrice: f1, count: n, totalQty: q, leverage: lev });
        const vars = { count: r.count, qty: num(r.qtyEach, m.qtyDp), base: m.base, from: price(symbol, f0), to: price(symbol, f1) };
        toast(isLong ? t('{count} buy orders placed: {qty} {base} each from {from} to {to}', vars) : t('{count} sell orders placed: {qty} {base} each from {from} to {to}', vars), 'success');
        setQty('');
      } catch (err) { toast(tErr(err.message), 'error'); }
      finally { setBusy(false); }
      return undefined;
    }
    try {
      const o = await api('POST', `/api/accounts/${account.id}/orders`, {
        symbol, side, type, qty: q, leverage: lev, ...(type === 'limit' ? { limitPrice: Number(limit) } : {}),
        ...(protect && slN ? { slPrice: slN } : {}), ...(protect && tpN ? { tpPrice: tpN } : {}),
      });
      const vars = { qty: num(o.qty, m.qtyDp), base: m.base, price: price(symbol, o.status === 'filled' ? o.fillPrice : o.limitPrice) };
      if (o.status === 'filled') toast(isLong ? t('Bought {qty} {base} at {price}', vars) : t('Sold {qty} {base} at {price}', vars), 'success');
      else toast(isLong ? t('Limit order placed: buy {qty} {base} at {price}', vars) : t('Limit order placed: sell {qty} {base} at {price}', vars), 'info');
      setQty(''); setSl(''); setTp('');
    } catch (err) { toast(tErr(err.message), 'error'); }
    finally { setBusy(false); }
    return undefined;
  };
  const stepTxt = ladder.length > 1 ? num(Math.abs(ladder[1] - ladder[0]), m.priceDp) : null;
  const fillFrom = () => {
    if (!last) return;
    const d = last * 0.002, d2 = last * 0.03;
    if (isLong) { setFrom(String(Number((last - d).toFixed(m.priceDp)))); setTo(String(Number((last - d2).toFixed(m.priceDp)))); }
    else { setFrom(String(Number((last + d).toFixed(m.priceDp)))); setTo(String(Number((last + d2).toFixed(m.priceDp)))); }
  };
  useEffect(() => { if (type === 'scaled' && !from && !to && last) fillFrom(); }, [type, side]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <form className="order" aria-label={t('Place order')} onSubmit={submit}>
      <div className="order-prefs">
        <button type="button" className={'pref' + (prefs.oneClick ? ' on' : '')} aria-pressed={prefs.oneClick} onClick={() => setPrefs({ oneClick: !prefs.oneClick })} title={t('Buy / Sell buttons on the chart that trade instantly at market')}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M13 2L3 14h8l-1 8 10-12h-8z" /></svg>{t('1-Click')}
        </button>
        <button type="button" className={'pref' + (prefs.sound ? ' on' : '')} aria-pressed={prefs.sound} onClick={() => setPrefs({ sound: !prefs.sound })} title={t('Play a sound when orders fill')}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M11 5L6 9H2v6h4l5 4z" />{prefs.sound ? <path d="M15.5 8.5a5 5 0 010 7M18.5 5.5a9 9 0 010 13" /> : <path d="M22 9l-6 6M16 9l6 6" />}</svg>{t('Sound')}
        </button>
        <div className="grow" />
        <div className="menu-wrap" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="pref icon" aria-label={t('Trading settings')} aria-expanded={settings} onClick={() => setSettings(!settings)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 11-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 11-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 11-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 110-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 112.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 114 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 112.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 110 4h-.1a1.7 1.7 0 00-1.5 1z" /></svg>
          </button>
          {settings && (
            <div className="menu right settings-menu">
              <label className="menu-item check-item"><input type="checkbox" checked={prefs.oneClick} onChange={(e) => setPrefs({ oneClick: e.target.checked })} /><span>{t('One-click trading on the chart')}</span></label>
              <label className="menu-item check-item"><span>{t('One-click leverage')}</span><select value={prefs.qcLev} onChange={(e) => setPrefs({ qcLev: Number(e.target.value) })}>{[1, 2, 3, 5, 10, 20, 50].filter((x) => x <= account.maxLeverage).map((x) => <option key={x} value={x}>{x}×</option>)}</select></label>
              <label className="menu-item check-item"><input type="checkbox" checked={prefs.sound} onChange={(e) => setPrefs({ sound: e.target.checked })} /><span>{t('Sound when orders fill')}</span></label>
              <label className="menu-item check-item"><input type="checkbox" checked={prefs.keys} onChange={(e) => setPrefs({ keys: e.target.checked })} /><span>{t('Keyboard shortcuts')}</span></label>
              <button type="button" className="menu-item" onClick={() => { setSettings(false); showHelp(); }}>{t('Show keyboard shortcuts')} <kbd>?</kbd></button>
            </div>
          )}
        </div>
      </div>
      <div className="seg">
        <button type="button" className={'seg-btn' + (isLong ? ' long' : '')} onClick={() => setSide('buy')} aria-pressed={isLong}>{t('Long')}</button>
        <button type="button" className={'seg-btn' + (!isLong ? ' short' : '')} onClick={() => setSide('sell')} aria-pressed={!isLong}>{t('Short')}</button>
      </div>
      <div className="tabs">
        <button type="button" className={'tab' + (type === 'market' ? ' on' : '')} onClick={() => setType('market')}>{t('Market')}</button>
        <button type="button" className={'tab' + (type === 'limit' ? ' on' : '')} onClick={() => { setType('limit'); if (!limit && last) setLimit(String(last)); }}>{t('Limit')}</button>
        <button type="button" className={'tab' + (type === 'scaled' ? ' on' : '')} onClick={() => setType('scaled')} title={t('Several limit orders spread between two prices')}>{t('Scaled')}</button>
      </div>
      {type === 'scaled' && (
        <div className="scaled">
          <div className="sltp">
            <div className="field">
              <label htmlFor="sc-from">{isLong ? t('From (highest)') : t('From (lowest)')}</label>
              <div className="input sm"><input id="sc-from" inputMode="decimal" value={from} onChange={(e) => setFrom(e.target.value.replace(/[^0-9.]/g, ''))} /></div>
            </div>
            <div className="field">
              <label htmlFor="sc-to">{isLong ? t('To (lowest)') : t('To (highest)')}</label>
              <div className="input sm"><input id="sc-to" inputMode="decimal" value={to} onChange={(e) => setTo(e.target.value.replace(/[^0-9.]/g, ''))} /></div>
            </div>
          </div>
          <div className="field">
            <div className="field-head"><label htmlFor="sc-n">{t('Number of orders')}</label><span className="mono">{n || '—'}</span></div>
            <input id="sc-n" type="range" min="2" max="25" step="1" value={n || 2} onChange={(e) => setCount(e.target.value)} style={{ '--p': (((n || 2) - 2) / 23) * 100 + '%' }} />
          </div>
          {ladder.length > 0 && (
            <div className="ladder-preview">
              <span>{t('{n} orders · every {step} · {qty} {base} each', { n, step: stepTxt, qty: eachQty > 0 ? num(eachQty, m.qtyDp) : '—', base: m.base })}</span>
              <span className="muted">{t('Average price {price}', { price: price(symbol, ladderAvg) })}</span>
              <div className="ladder-prices mono">{ladder.length <= 6 ? ladder.map((x) => price(symbol, x)).join(' · ') : `${price(symbol, ladder[0])} · ${price(symbol, ladder[1])} · ${price(symbol, ladder[2])} … ${price(symbol, ladder[n - 1])}`}</div>
            </div>
          )}
        </div>
      )}
      {type === 'limit' && (
        <div className="field">
          <label htmlFor="limit-price">{t('Limit price')}</label>
          <div className="input"><input id="limit-price" inputMode="decimal" value={limit} onChange={(e) => setLimit(e.target.value.replace(/[^0-9.]/g, ''))} /><span className="unit">{m.quote === 'USDT' ? 'USD' : m.quote}</span></div>
        </div>
      )}
      <div className="field">
        <div className="field-head"><label htmlFor="order-size">{type === 'scaled' ? t('Total size') : t('Size')}</label><span className={'label' + (tooBig ? ' warn-text' : '')}>{t('Max {qty} {base}', { qty: num(maxQty, m.qtyDp), base: m.base })}</span></div>
        <div className="input"><input id="order-size" ref={sizeRef} inputMode="decimal" placeholder={m.category === 'forex' ? '10000' : '0.00'} value={qty} onChange={(e) => setQty(e.target.value.replace(/[^0-9.]/g, ''))} /><span className="unit">{m.base} ≈ {usd(notional)}</span></div>
        <div className="fracs">
          {[0.25, 0.5, 0.75, 1].map((f) => <button type="button" key={f} className="frac" onClick={() => setFrac(f)}>{f * 100}%</button>)}
        </div>
      </div>
      <div className="field">
        <div className="field-head"><label htmlFor="lev">{t('Leverage')}</label><span className="mono">{lev}×</span></div>
        <input id="lev" type="range" min="1" max={maxLev} step="1" value={lev} onChange={(e) => setLev(Number(e.target.value))} style={{ '--p': ((lev - 1) / (maxLev - 1 || 1)) * 100 + '%' }} />
        <div className="ticks mono"><span>1×</span><span>{Math.round(maxLev / 2)}×</span><span>{maxLev}×</span></div>
      </div>
      {type !== 'scaled' && <label className="check">
        <input type="checkbox" checked={protect} onChange={(e) => setProtect(e.target.checked)} />
        <span>{t('Stop-loss / Take-profit')}</span>
      </label>}
      {protect && type !== 'scaled' && (
        <div className="sltp">
          <div className="field">
            <div className="field-head"><label htmlFor="sl">{t('Stop-loss')}</label><span className={'label ' + (atLevel(slN) != null ? 'down' : '')}>{atLevel(slN) != null ? signedUsd(atLevel(slN)) : ''}</span></div>
            <div className="input sm"><input id="sl" inputMode="decimal" placeholder={isLong ? t('Below price') : t('Above price')} value={sl} onChange={(e) => setSl(e.target.value.replace(/[^0-9.]/g, ''))} /></div>
          </div>
          <div className="field">
            <div className="field-head"><label htmlFor="tp">{t('Take-profit')}</label><span className={'label ' + (atLevel(tpN) != null ? 'up' : '')}>{atLevel(tpN) != null ? signedUsd(atLevel(tpN)) : ''}</span></div>
            <div className="input sm"><input id="tp" inputMode="decimal" placeholder={isLong ? t('Above price') : t('Below price')} value={tp} onChange={(e) => setTp(e.target.value.replace(/[^0-9.]/g, ''))} /></div>
          </div>
        </div>
      )}
      <div className="summary">
        <div><span className="label">{t('Margin required')}</span><span className="mono">{usd(margin)}</span></div>
        <div><span className="label">{t('Est. fee ({rate}%)', { rate: num(feeRate * 100, feeRate < 0.0001 ? 3 : 2) })}</span><span className="mono">{usd(fee)}</span></div>
        <div title={t('Fees to open and later close this position at the same price')}><span className="label">{t('Round-trip fees')}</span><span className="mono">{usd(feeRound)}</span></div>
        <div><span className="label">{t('Free margin')}</span><span className="mono">{usd(account.freeMargin)}</span></div>
      </div>
      {q > 0 && px > 0 && (
        <div className={'risk' + (onePct > lossRoom ? ' danger' : '')}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 9v4M12 17h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" /></svg>
          <span>
            {onePct > lossRoom
              ? t('A 1% move against you ({move}) would breach your account. You have {room} of loss room left.', { move: usd(onePct), room: usd(lossRoom) })
              : t('A 1% move against you costs {move}, which is {share}% of your {room} remaining loss room.', { move: usd(onePct), share: num((onePct / (lossRoom || 1)) * 100, 0), room: usd(lossRoom, 0) })}
          </span>
        </div>
      )}
      <div className="grow" />
      <button type="submit" className={'btn submit ' + (isLong ? 'long' : 'short')} disabled={busy || !tradable || !last || !open || tooBig}>
        {!tradable ? t('Trading disabled') : !last ? t('Waiting for live prices…') : !open ? t('Market closed') : tooBig ? t('Not enough free margin') : busy ? t('Placing…')
          : type === 'scaled' ? `${isLong ? t('Place {n} buy orders', { n: n || '' }) : t('Place {n} sell orders', { n: n || '' })}${q ? ` · ${qty} ${m.base}` : ''}`
          : `${isLong ? t('Buy / Long') : t('Sell / Short')}${q ? ` ${qty} ${m.base}` : ''}`}
      </button>
    </form>
  );
}

// ---------------------------------------------------------------- bottom tabs
function BottomPanel({ account, toast, setSymbol, prices }) {
  const [tab, setTab] = useState('positions');
  const [orders, setOrders] = useState([]);
  const [events, setEvents] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [closing, setClosing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [partial, setPartial] = useState(null);
  const [journal, setJournal] = useState(null);
  const funded = account.stage === 'funded';

  const refresh = useCallback(async () => {
    try {
      if (tab === 'orders' || tab === 'history') setOrders(await api('GET', `/api/accounts/${account.id}/orders`));
      if (tab === 'log') setEvents(await api('GET', `/api/accounts/${account.id}/events`));
      if (tab === 'payouts') setPayouts((await api('GET', '/api/payouts')).filter((p) => p.accountId === account.id));
      if (tab === 'journal') setJournal(await api('GET', `/api/accounts/${account.id}/journal`));
    } catch { /* keep last */ }
  }, [tab, account.id]);
  useEffect(() => { refresh(); const t = setInterval(refresh, 3000); return () => clearInterval(t); }, [refresh]);

  const pending = orders.filter((o) => o.status === 'pending');
  const history = orders.filter((o) => o.status !== 'pending');

  const close = async (sym) => {
    setClosing(sym);
    try {
      const o = await api('POST', `/api/accounts/${account.id}/positions/${sym}/close`, {});
      toast(t('Closed {market}: {pnl} net after fees', { market: meta(sym).name, pnl: signedUsd(o.netPnl) }), o.netPnl >= 0 ? 'success' : 'info');
    } catch (e) { toast(tErr(e.message), 'error'); }
    finally { setClosing(null); }
  };
  const cancel = async (id) => {
    try { await api('DELETE', `/api/accounts/${account.id}/orders/${id}`); toast(t('Order cancelled'), 'info'); refresh(); }
    catch (e) { toast(tErr(e.message), 'error'); }
  };

  const tabs = [['positions', t('Positions'), account.positions.length], ['orders', t('Open orders'), tab === 'orders' ? pending.length : null],
    ['history', t('Trade history')], ['journal', t('Journal')], ['log', t('Rule log')], ...(funded ? [['payouts', t('Rewards')]] : [])];
  return (
    <section className="bottom" aria-label={t('Positions and orders')}>
      <div className="bottom-tabs">
        {tabs.map(([id, label, count]) => (
          <button key={id} className={'btab' + (tab === id ? ' on' : '')} onClick={() => setTab(id)}>{label}{count != null && <span className="count">{count}</span>}</button>
        ))}
        <div className="grow" />
        <span className="label hide-m" title={t('Price movement of open positions, before fees')}>{t('Unrealized PnL')} <span className={'mono ' + pnlClass(account.unrealizedPnl)}>{signedUsd(account.unrealizedPnl)}</span></span>
        {account.openNetPnl != null && <span className="label hide-m" title={t('Open positions after opening fees and estimated closing fees')}>{t('Open net')} <span className={'mono ' + pnlClass(account.openNetPnl)}>{signedUsd(account.openNetPnl)}</span></span>}
        <span className="label hide-m">{t('Margin used')} <span className="mono">{usd(account.usedMargin)}</span></span>
      </div>
      <div className="table-wrap">
        {tab === 'positions' && (
          account.positions.length === 0 ? <div className="empty">{t('No open positions')}</div> : (
            <table className="positions">
              <thead><tr><th>{t('Market')}</th><th>{t('Side')}</th><th>{t('Size')}</th><th>{t('Entry')}</th><th>{t('Mark')}</th><th title={t('Price movement, before fees')}>{t('PnL')}</th><th title={t('Opening fee paid + estimated closing fee')}>{t('Fees')}</th><th title={t('What you keep if you close now, after all fees')}>{t('Net PnL')}</th><th>{t('Margin')}</th><th>SL / TP</th><th className="r">{t('Close')}</th></tr></thead>
              <tbody>
                {account.positions.map((p) => {
                  const pm = meta(p.symbol);
                  return (
                    <tr key={p.id}>
                      <td data-label={t('Market')}><button className="link-market" onClick={() => setSymbol(p.symbol)}><MarketIcon symbol={p.symbol} size={16} />{pm.name} <span className="muted">{num(p.leverage, 0)}×</span></button></td>
                      <td data-label={t('Side')} className={p.side === 'long' ? 'up b' : 'down b'}>{p.side === 'long' ? t('Long') : t('Short')}</td>
                      <td data-label={t('Size')} className="mono">{num(p.qty, pm.qtyDp)} {pm.base}</td>
                      <td data-label={t('Entry')} className="mono">{price(p.symbol, p.entryPrice)}</td>
                      <td data-label={t('Mark')} className="mono">{price(p.symbol, p.markPrice)}</td>
                      <td data-label={t('PnL')} className={'mono ' + pnlClass(p.unrealizedPnl)}>{signedUsd(p.unrealizedPnl)} <span className="small">({pct(p.roePct)})</span></td>
                      <td data-label={t('Fees')} className="mono muted" title={t('Paid {paid} to open · about {fee} to close', { paid: usd(p.feesPaid), fee: usd(p.estCloseFee) })}>−{usd((p.feesPaid || 0) + (p.estCloseFee || 0))}</td>
                      <td data-label={t('Net PnL')} className={'mono b ' + pnlClass(p.netPnl)}>{p.netPnl == null ? '—' : signedUsd(p.netPnl)}</td>
                      <td data-label={t('Margin')} className="mono">{usd(p.margin)}</td>
                      <td data-label="SL / TP">
                        <button className="sltp-chip" onClick={() => setEditing(p)} aria-label={t('Edit stop-loss and take-profit for {market}', { market: pm.name })}>
                          <span className={p.slPrice ? 'down' : 'muted'}>{p.slPrice ? price(p.symbol, p.slPrice) : 'SL'}</span>
                          <span className="muted">/</span>
                          <span className={p.tpPrice ? 'up' : 'muted'}>{p.tpPrice ? price(p.symbol, p.tpPrice) : 'TP'}</span>
                        </button>
                      </td>
                      <td className="r">
                        <span className="close-btns">
                          <button className="btn sm" onClick={() => setPartial(p)} disabled={closing === p.symbol} title={t('Close part of this position')}>{t('Partial')}</button>
                          <button className="btn sm" onClick={() => close(p.symbol)} disabled={closing === p.symbol}>{closing === p.symbol ? t('Closing…') : t('Close all')}</button>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        )}
        {tab === 'orders' && (
          pending.length === 0 ? <div className="empty">{t('No open orders')}</div> : (
            <table>
              <thead><tr><th>{t('Market')}</th><th>{t('Side')}</th><th>{t('Type')}</th><th>{t('Size')}</th><th>{t('Price')}</th><th>SL / TP</th><th>{t('Placed')}</th><th className="r">{t('Action')}</th></tr></thead>
              <tbody>
                {pending.map((o) => (
                  <tr key={o.id}>
                    <td data-label={t('Market')}><span className="row gap8"><MarketIcon symbol={o.symbol} size={16} />{meta(o.symbol).name}</span></td>
                    <td data-label={t('Side')} className={o.side === 'buy' ? 'up b' : 'down b'}>{o.side === 'buy' ? t('Buy') : t('Sell')}</td>
                    <td data-label={t('Type')}>{o.reduceOnly ? (o.type === 'stop' ? t('Stop close') : t('Close at target')) : t('Limit')}</td>
                    <td data-label={t('Size')} className="mono">{o.qty}</td>
                    <td data-label={t('Price')} className="mono">{price(o.symbol, o.limitPrice)}</td>
                    <td data-label="SL / TP" className="mono muted">{o.slPrice ? price(o.symbol, o.slPrice) : '—'} / {o.tpPrice ? price(o.symbol, o.tpPrice) : '—'}</td>
                    <td data-label={t('Placed')} className="muted">{fmtTime(o.createdAt)}</td>
                    <td className="r"><button className="btn sm" onClick={() => cancel(o.id)}>{t('Cancel')}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
        {tab === 'history' && (
          history.length === 0 ? <div className="empty">{t('No trades yet')}</div> : (
            <table>
              <thead><tr><th>{t('Time')}</th><th>{t('Market')}</th><th>{t('Side')}</th><th>{t('Size')}</th><th>{t('Price')}</th><th>{t('Fee')}</th><th title={t('Price result of the part closed, before fees')}>{t('Gross PnL')}</th><th title={t('Gross PnL minus the closing fee and the opening fee of the part closed')}>{t('Net PnL')}</th><th>{t('Note')}</th></tr></thead>
              <tbody>
                {history.map((o) => (
                  <tr key={o.id}>
                    <td data-label={t('Time')} className="muted">{fmtDateTime(o.filledAt || o.createdAt)}</td>
                    <td data-label={t('Market')}>{meta(o.symbol).name}</td>
                    <td data-label={t('Side')} className={o.side === 'buy' ? 'up b' : 'down b'}>{o.side === 'buy' ? t('Buy') : t('Sell')}</td>
                    <td data-label={t('Size')} className="mono">{o.qty}</td>
                    <td data-label={t('Price')} className="mono">{o.fillPrice ? price(o.symbol, o.fillPrice) : '—'}</td>
                    <td data-label={t('Fee')} className="mono">{usd(o.fee)}</td>
                    <td data-label={t('Gross PnL')} className={'mono ' + pnlClass(o.realizedPnl)}>{o.closeQty > 0 || o.realizedPnl ? signedUsd(o.realizedPnl) : '—'}</td>
                    <td data-label={t('Net PnL')} className={'mono b ' + (o.status === 'filled' && (o.closeQty > 0 || o.realizedPnl) ? pnlClass(o.netPnl) : 'muted')}>{o.status === 'filled' && (o.closeQty > 0 || o.realizedPnl) ? signedUsd(o.netPnl) : '—'}</td>
                    <td data-label={t('Note')} className="muted">{TRIGGER_LABEL[o.trigger] ? t(TRIGGER_LABEL[o.trigger]) : (o.status !== 'filled' ? statusLabel(o.status) : o.closeQty > 0 ? (o.closeQty < o.qty - 1e-9 ? t('Close + reverse') : t('Close')) : t('Open'))}{o.rejectReason ? `: ${tErr(o.rejectReason)}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
        {tab === 'journal' && <Journal data={journal} />}
        {tab === 'log' && (
          events.length === 0 ? <div className="empty">{t('No events yet')}</div> : (
            <table>
              <thead><tr><th>{t('Time')}</th><th>{t('Event')}</th><th>{t('Details')}</th></tr></thead>
              <tbody>
                {events.map((e, i) => (
                  <tr key={i}>
                    <td data-label={t('Time')} className="muted">{fmtDateTime(e.created_at)}</td>
                    <td data-label={t('Event')} className={e.kind === 'breach' || e.kind === 'stop_loss' ? 'down b' : e.kind === 'stage_advance' || e.kind === 'take_profit' ? 'up b' : 'b'}>{EVENT_LABEL[e.kind] ? t(EVENT_LABEL[e.kind]) : e.kind}</td>
                    <td data-label={t('Details')} className="muted wrap">{describeEvent(e)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
        {tab === 'payouts' && (
          payouts.length === 0 ? <div className="empty">{t('No reward requests yet. Use “Request reward” above when you have closed virtual profit.')}</div> : (
            <table>
              <thead><tr><th>{t('Requested')}</th><th>{t('Virtual profit')}</th><th>{t('Rate')}</th><th>{t('Reward')}</th><th>{t('Status')}</th><th>{t('Transaction')}</th></tr></thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.id}>
                    <td data-label={t('Requested')} className="muted">{fmtDateTime(p.createdAt)}</td>
                    <td data-label={t('Profit')} className="mono">{usd(p.grossProfit)}</td>
                    <td data-label={t('Split')} className="mono">{Math.round(p.split * 100)}%</td>
                    <td data-label={t('Amount')} className="mono up">{usd(p.amount)}</td>
                    <td data-label={t('Status')}><span className={'badge ' + (p.status === 'paid' ? 'active' : p.status === 'rejected' ? 'breached' : '')}>{statusLabel(p.status)}</span></td>
                    <td data-label="Tx" className="mono muted">{p.txHash ? `${p.txHash.slice(0, 10)}…` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>
      {editing && <ProtectionModal account={account} position={editing} prices={prices} toast={toast} onClose={() => setEditing(null)} />}
      {partial && <PartialCloseModal account={account} position={account.positions.find((x) => x.symbol === partial.symbol) || partial} toast={toast} onClose={() => setPartial(null)} />}
    </section>
  );
}

function PartialCloseModal({ account, position: p, toast, onClose }) {
  const m = meta(p.symbol);
  const [pctV, setPctV] = useState(50);
  const [size, setSize] = useState('');
  const [mode, setMode] = useState('market'); // market | limit (target price) | stop
  const [at, setAt] = useState('');
  const [busy, setBusy] = useState(false);
  const step = m.qtyStep;
  const isLong = p.side === 'long';
  const fromPct = (v) => Math.floor((p.qty * v / 100) / step + 1e-9) * step;
  const q = size !== '' ? Math.min(Number(size) || 0, p.qty) : (pctV >= 100 ? p.qty : fromPct(pctV));
  const share = p.qty ? q / p.qty : 0;
  const mark = p.markPrice;
  const atNum = Number(at);
  const waiting = mode !== 'market';
  // Price the estimate uses: now, or the chosen close price.
  const ref = waiting ? (atNum > 0 ? atNum : null) : mark;
  const dir = isLong ? 1 : -1;
  const moved = (mark - p.entryPrice) * dir * p.qty;
  const conv = mark && Math.abs(moved) > 1e-12 ? p.unrealizedPnl / moved : 1;
  const gross = ref == null ? null : (waiting ? (ref - p.entryPrice) * dir * q * conv : p.unrealizedPnl * share);
  const fees = ((p.feesPaid || 0) + (p.estCloseFee || 0) * (ref && mark ? ref / mark : 1)) * share;
  const net = gross == null ? null : gross - fees;
  let priceErr = '';
  if (waiting && atNum > 0 && mark) {
    if (mode === 'limit' && isLong && atNum <= mark) priceErr = t('For a long, the target price must be above the current price');
    if (mode === 'limit' && !isLong && atNum >= mark) priceErr = t('For a short, the target price must be below the current price');
    if (mode === 'stop' && isLong && atNum >= mark) priceErr = t('For a long, the stop price must be below the current price');
    if (mode === 'stop' && !isLong && atNum <= mark) priceErr = t('For a short, the stop price must be above the current price');
  }
  const pickMode = (v) => { setMode(v); if (v !== 'market' && !at && mark) setAt(String(Number(mark.toFixed(m.priceDp)))); };
  const submit = async () => {
    if (!q || (waiting && (!(atNum > 0) || priceErr))) return;
    setBusy(true);
    try {
      const body = { ...(size !== '' ? { qty: q } : { percent: pctV }), ...(waiting ? { type: mode, price: atNum } : {}) };
      const o = await api('POST', `/api/accounts/${account.id}/positions/${p.symbol}/close`, body);
      if (waiting) {
        toast(mode === 'stop'
          ? t('Stop close placed: {qty} {base} closes if the price reaches {price}. See the Open orders tab.', { qty: num(o.qty, m.qtyDp), base: m.base, price: price(p.symbol, o.limitPrice) })
          : t('Close order placed: {qty} {base} at {price}. See the Open orders tab.', { qty: num(o.qty, m.qtyDp), base: m.base, price: price(p.symbol, o.limitPrice) }), 'success');
      } else {
        toast(t('Closed {qty} {base} of {market}: {pnl} net after fees', { qty: num(o.qty, m.qtyDp), base: m.base, market: m.name, pnl: signedUsd(o.netPnl) }), o.netPnl >= 0 ? 'success' : 'info');
      }
      onClose();
    } catch (e) { toast(tErr(e.message), 'error'); }
    finally { setBusy(false); }
  };
  const label = mode === 'market'
    ? t('Close {qty} {base} at market', { qty: num(q, m.qtyDp), base: m.base })
    : mode === 'limit'
      ? t('Place close order: {qty} {base} at {price}', { qty: num(q, m.qtyDp), base: m.base, price: atNum > 0 ? price(p.symbol, atNum) : '—' })
      : t('Place stop: close {qty} {base} at {price}', { qty: num(q, m.qtyDp), base: m.base, price: atNum > 0 ? price(p.symbol, atNum) : '—' });
  return (
    <Modal title={isLong ? t('Close part of {market} long', { market: m.name }) : t('Close part of {market} short', { market: m.name })} onClose={onClose}>
      <div className="modal-body">
        <div className="summary">
          <div><span className="label">{t('Position size')}</span><span className="mono">{num(p.qty, m.qtyDp)} {m.base}</span></div>
          <div><span className="label">{t('Entry / current price')}</span><span className="mono">{price(p.symbol, p.entryPrice)} / {mark ? price(p.symbol, mark) : '—'}</span></div>
          <div><span className="label">{t('Net PnL now (whole position)')}</span><span className={'mono ' + pnlClass(p.netPnl)}>{signedUsd(p.netPnl)}</span></div>
        </div>
        <div className="tabs">
          <button type="button" className={'tab' + (mode === 'market' ? ' on' : '')} onClick={() => pickMode('market')}>{t('Now (market)')}</button>
          <button type="button" className={'tab' + (mode === 'limit' ? ' on' : '')} onClick={() => pickMode('limit')} title={t('Close this part when the price reaches your target')}>{t('At target price')}</button>
          <button type="button" className={'tab' + (mode === 'stop' ? ' on' : '')} onClick={() => pickMode('stop')} title={t('Close this part if the price moves against you to this level')}>{t('Stop')}</button>
        </div>
        {waiting && (
          <div className="field">
            <label htmlFor="pc-at">{mode === 'limit' ? t('Target price') : t('Stop price')}</label>
            <div className="input"><input id="pc-at" inputMode="decimal" value={at} onChange={(e) => setAt(e.target.value.replace(',', '.').replace(/[^0-9.]/g, ''))} /><span className="unit">{m.quote === 'USDT' ? 'USD' : m.quote}</span></div>
            <div className={'small ' + (priceErr ? 'warn-text' : 'muted')}>{priceErr || (mode === 'limit'
              ? (isLong ? t('Waits until the price rises to this level, then closes this part.') : t('Waits until the price falls to this level, then closes this part.'))
              : (isLong ? t('If the price falls to this level, this part closes at market.') : t('If the price rises to this level, this part closes at market.')))}</div>
          </div>
        )}
        <div className="fracs big">
          {[25, 50, 75, 100].map((v) => <button type="button" key={v} className={'frac' + (size === '' && pctV === v ? ' on' : '')} onClick={() => { setPctV(v); setSize(''); }}>{v}%</button>)}
        </div>
        <div className="field">
          <div className="field-head"><label htmlFor="pc-pct">{t('Close')}</label><span className="mono">{size === '' ? `${pctV}%` : `${num(share * 100, 1)}%`}</span></div>
          <input id="pc-pct" type="range" min="1" max="100" step="1" value={size === '' ? pctV : Math.round(share * 100)} onChange={(e) => { setPctV(Number(e.target.value)); setSize(''); }} style={{ '--p': (((size === '' ? pctV : share * 100) - 1) / 99) * 100 + '%' }} />
        </div>
        <div className="field">
          <label htmlFor="pc-size">{t('Or exact size')}</label>
          <div className="input"><input id="pc-size" inputMode="decimal" placeholder={num(fromPct(pctV), m.qtyDp)} value={size} onChange={(e) => setSize(e.target.value.replace(/[^0-9.]/g, ''))} /><span className="unit">{m.base}</span></div>
        </div>
        <div className="summary">
          <div><span className="label">{t('Closing')}</span><span className="mono">{num(q, m.qtyDp)} {m.base}</span></div>
          <div><span className="label">{waiting ? t('Gross PnL on this part at that price') : t('Gross PnL on this part')}</span><span className={'mono ' + pnlClass(gross)}>{gross == null ? '—' : signedUsd(gross)}</span></div>
          <div><span className="label">{t('Fees on this part')}</span><span className="mono">−{usd(fees)}</span></div>
          <div className="total"><span>{t('Net result (estimate)')}</span><span className={'mono ' + pnlClass(net)}>{net == null ? '—' : signedUsd(net)}</span></div>
        </div>
        <button className="btn primary lg block" onClick={submit} disabled={busy || !q || (waiting && (!(atNum > 0) || !!priceErr))}>{busy ? t('Closing…') : label}</button>
      </div>
    </Modal>
  );
}

function Journal({ data }) {
  if (!data) return <div className="empty">{t('Loading journal…')}</div>;
  const s = data.stats;
  if (!s.trades) return <div className="empty">{t('Your journal fills in as you close trades: win rate, average win and loss, best day and more.')}</div>;
  const maxDay = Math.max(1, ...data.days.map((d) => Math.abs(d.netPnl)));
  const cards = [
    [t('Net PnL (closed)'), <span className={pnlClass(s.netPnl)}>{signedUsd(s.netPnl)}</span>, t('After all fees')],
    [t('Win rate'), `${num(s.winRate, 1)}%`, t('{wins} wins · {losses} losses', { wins: s.wins, losses: s.losses })],
    [t('Trades'), s.trades, t('Closing fills, including partial closes')],
    [t('Profit factor'), s.profitFactor == null ? '∞' : num(s.profitFactor, 2), t('Total wins ÷ total losses')],
    [t('Average win'), <span className="up">{signedUsd(s.avgWin)}</span>, ''],
    [t('Average loss'), <span className="down">{signedUsd(s.avgLoss)}</span>, ''],
    [t('Best trade'), <span className={pnlClass(s.best)}>{signedUsd(s.best)}</span>, ''],
    [t('Worst trade'), <span className={pnlClass(s.worst)}>{signedUsd(s.worst)}</span>, ''],
    [t('Fees paid'), usd(s.totalFees), t('All fills')],
    [t('Max drawdown (closed)'), usd(s.maxDrawdown), t('Largest fall from a peak')],
  ];
  return (
    <div className="journal">
      <div className="j-cards">
        {cards.map(([label, value, hint]) => <div key={label} className="j-card"><span className="label">{label}</span><span className="mono j-v">{value}</span>{hint && <span className="label">{hint}</span>}</div>)}
      </div>
      <div className="j-cols">
        <div className="j-box">
          <div className="j-title">{t('Net PnL by day')}</div>
          <div className="j-days">
            {data.days.slice(-30).map((d) => (
              <div key={d.day} className="j-day" title={`${d.day}: ${signedUsd(d.netPnl)}`}>
                <div className={'j-bar ' + (d.netPnl >= 0 ? 'pos' : 'neg')} style={{ height: Math.max(2, (Math.abs(d.netPnl) / maxDay) * 100) + '%' }} />
                <span className="j-dlabel">{d.day.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="j-box">
          <div className="j-title">{t('By market')}</div>
          <table>
            <thead><tr><th>{t('Market')}</th><th>{t('Trades')}</th><th>{t('Win rate')}</th><th className="r">{t('Net PnL')}</th></tr></thead>
            <tbody>{data.bySymbol.map((x) => (
              <tr key={x.symbol}><td data-label={t('Market')}><span className="row gap8"><MarketIcon symbol={x.symbol} size={16} />{meta(x.symbol).name}</span></td>
                <td data-label={t('Trades')} className="mono">{x.trades}</td><td data-label={t('Win rate')} className="mono">{num((x.wins / x.trades) * 100, 0)}%</td>
                <td data-label={t('Net PnL')} className={'mono r ' + pnlClass(x.netPnl)}>{signedUsd(x.netPnl)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ProtectionModal({ account, position: p, prices, toast, onClose }) {
  const m = meta(p.symbol);
  const [sl, setSl] = useState(p.slPrice ? String(p.slPrice) : '');
  const [tp, setTp] = useState(p.tpPrice ? String(p.tpPrice) : '');
  const [busy, setBusy] = useState(false);
  const conv = usdPerQuote(p.symbol, prices) || 1;
  const at = (lvl) => (lvl ? (p.side === 'long' ? lvl - p.entryPrice : p.entryPrice - lvl) * p.qty * conv : null);
  const save = async () => {
    setBusy(true);
    try {
      await api('PATCH', `/api/accounts/${account.id}/positions/${p.symbol}`, { slPrice: sl ? Number(sl) : null, tpPrice: tp ? Number(tp) : null });
      toast(t('Stop-loss / take-profit saved'), 'success');
      onClose();
    } catch (e) { toast(tErr(e.message), 'error'); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={p.side === 'long' ? t('{market} long: stop-loss / take-profit', { market: m.name }) : t('{market} short: stop-loss / take-profit', { market: m.name })} onClose={onClose}>
      <div className="modal-body">
        <div className="summary"><div><span className="label">{t('Entry')}</span><span className="mono">{price(p.symbol, p.entryPrice)}</span></div><div><span className="label">{t('Mark')}</span><span className="mono">{price(p.symbol, p.markPrice)}</span></div></div>
        <div className="field">
          <div className="field-head"><label htmlFor="msl">{t('Stop-loss')}</label><span className="label down">{at(Number(sl)) != null ? t('{pnl} from entry', { pnl: signedUsd(at(Number(sl))) }) : t('Empty = none')}</span></div>
          <div className="input"><input id="msl" inputMode="decimal" value={sl} onChange={(e) => setSl(e.target.value.replace(/[^0-9.]/g, ''))} placeholder={p.side === 'long' ? t('Below the mark price') : t('Above the mark price')} /></div>
        </div>
        <div className="field">
          <div className="field-head"><label htmlFor="mtp">{t('Take-profit')}</label><span className="label up">{at(Number(tp)) != null ? t('{pnl} from entry', { pnl: signedUsd(at(Number(tp))) }) : t('Empty = none')}</span></div>
          <div className="input"><input id="mtp" inputMode="decimal" value={tp} onChange={(e) => setTp(e.target.value.replace(/[^0-9.]/g, ''))} placeholder={p.side === 'long' ? t('Above the mark price') : t('Below the mark price')} /></div>
        </div>
        <button className="btn primary lg block" onClick={save} disabled={busy}>{busy ? t('Saving…') : t('Save')}</button>
      </div>
    </Modal>
  );
}

// Shown after a waiting close order is cancelled because its position was already closed.
tk('Position closed');
const TRIGGER_LABEL = { stop_loss: tk('Stop-loss'), take_profit: tk('Take-profit'), breach: tk('Closed by breach'), limit_close: tk('Close at target'), stop_close: tk('Stop close') };
const EVENT_LABEL = {
  breach: tk('Breach'), stage_advance: tk('Stage passed'), day_rollover: tk('New trading day'), purchased: tk('Challenge purchased'),
  stop_loss: tk('Stop-loss hit'), take_profit: tk('Take-profit hit'), payout_requested: tk('Reward requested'), payout_paid: tk('Reward paid'),
  payout_rejected: tk('Reward declined'), admin_action: tk('Admin action'),
};
function describeEvent(e) {
  const d = e.detail || {};
  if (e.kind === 'breach') return d.reason ? tErr(d.reason) : d.reason;
  if (e.kind === 'stage_advance') return t('{from} → {to} with {balance}', { from: STAGE_LABEL[d.from], to: STAGE_LABEL[d.to], balance: usd(d.finalBalance) });
  if (e.kind === 'day_rollover') return t('Daily loss baseline set to {amount}', { amount: usd(d.dayStartEquity) });
  if (e.kind === 'purchased') {
    if (d.payment === 'onchain') return d.coupon ? t('{amount} paid with code {code}', { amount: usd(d.priceUsd), code: d.coupon }) : t('{amount} paid', { amount: usd(d.priceUsd) });
    return d.coupon ? t('Demo (no payment), code {code}', { code: d.coupon }) : t('Demo (no payment)');
  }
  if (e.kind === 'stop_loss' || e.kind === 'take_profit') return t('{market} closed at {price}: {pnl}', { market: meta(d.symbol).name, price: price(d.symbol, d.price), pnl: signedUsd(d.realized) });
  if (e.kind === 'payout_requested') return t('{amount} ({rate}% of {profit})', { amount: usd(d.amount), rate: Math.round(d.split * 100), profit: usd(d.profit) });
  if (e.kind === 'payout_paid') return t('{amount} sent', { amount: usd(d.amount) });
  if (e.kind === 'payout_rejected') return d.note || t('Virtual profit returned to the account');
  return JSON.stringify(d);
}
